import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWizard } from 'react-use-wizard';
import { useQueryClient } from '@tanstack/react-query';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@design-system';
import { ShopInsuranceNotice } from '@/features/shop-insurance';
import { AiUnderwritingCtaAlert } from '@/shared/components/ai-underwriting-cta-alert';
import { UnderwritingSlideOut } from '@/components/UnderwritingSlideOut';
import { cn } from '@client/lib';
import { FundingPolicyType } from '@shared/appModel';

import { getStepIndex } from '@/features/guided-financial-planning/config';
import { guidedPlanningQuoteKeys } from '@/features/guided-financial-planning/api';

import { useSession, useIsSessionComplete } from '@/features/guided-financial-planning/context';
import {
  useAddPolicyFromQuote,
  useGuidedPlanningQuotes,
  useQuotesDisplay,
  useUnderwritingResults,
  useCoverageTypeToggle,
  getQuoteId,
  getPaymentOption,
  getPolicyTypeFilterOptions,
  getPaymentOptionFilterOptions,
  type GuidedPlanningQuote,
} from '@/features/guided-financial-planning/hooks';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import { QuoteCard } from '@/features/guided-financial-planning/components/quote-card';
import { QuoteFiltersPanel } from '@/features/guided-financial-planning/components/quote-filters-panel';
import { GuidedPlanningFooter } from '@/features/guided-financial-planning/components/guided-planning-footer';
import { QuotesLoadingState, QuotesErrorState, QuotesEmptyState } from './quotes-ui-states';
import {
  AddPartnerDialog,
  LinkExistingPartnerDialog,
  PartnerRequiredDialog,
} from '@/features/add-partner';
import { NoQuotesNYScreen } from '@/components/NoQuotesNYScreen';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useSelector } from 'react-redux';
import { selectClient, selectSession, selectSessionAdvisor } from '@client/model/selectors';
import { SessionType, zipCodeToState } from '@shared';

const ALL_POLICY_TYPES = 'all';
const ALL_PAYMENT_OPTIONS = 'all';

/**
 * Checks if a quote is a joint/couples policy type
 */
function isJointOrCouplesQuote(quote: GuidedPlanningQuote): boolean {
  if ('policyType' in quote) {
    const policyQuote = quote as PolicyQuote;
    return policyQuote.policyType === 'Joint Life'; //|| policyQuote.policyType === 'Couples Discount' - subject to approval
  }
  return false;
}

/**
 * Finds the underwriting result for a quote by fuzzy-matching the product name.
 * Handles trademark symbols and version suffixes in product names.
 * Returns both the status and the full results text for displaying in slideout.
 */
function getUnderwritingResultForQuote(
  productName: string,
  underwritingResults: Record<string, UnderwritingResult> | undefined,
  isJointQuote: boolean = false,
): UnderwritingResult | undefined {
  if (!underwritingResults || !productName) return undefined;

  // Normalize the product name: lowercase, remove trademarks, trim
  const normalizedQuoteName = productName.toLowerCase().replace(/[®™©]/g, '').trim();

  // Try to find a matching key in the results
  for (const [key, result] of Object.entries(underwritingResults)) {
    const normalizedKey = key.toLowerCase().replace(/[®™©]/g, '').trim();

    const productNamesMatch =
      normalizedQuoteName.includes(normalizedKey) || normalizedKey.includes(normalizedQuoteName);

    // Check if the joint status matches
    const keyIsJoint = normalizedKey.includes('joint');
    const jointStatusMatches = isJointQuote === keyIsJoint;

    if (productNamesMatch && jointStatusMatches) {
      return result;
    }
  }

  return undefined;
}

export function QuotesSelectionStep() {
  const { flags } = useFeatureFlags();
  const queryClient = useQueryClient();
  const { nextStep, goToStep } = useWizard();
  const { session } = useSession();
  const clientId = session?.clientId ?? '';
  const isSessionComplete = useIsSessionComplete();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: quotes,
    isLoading,
    isError,
    noQuotesReason,
    isRefetching,
  } = useGuidedPlanningQuotes(clientId, session);

  const {
    selectedCoverageType,
    setSelectedCoverageType,
    handleCoverageTypeChange,
    isMarriedOrPartnered,
    showPartnerRequiredDialog,
    setShowPartnerRequiredDialog,
    availableTabs,
    filterQuotesByCoverageType,
  } = useCoverageTypeToggle();

  const client = useSelector(selectClient);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const { sessionType } = useSelector(selectSession);
  const isAdvisor = sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const clientState = zipCodeToState(client?.intakeSurvey?.clientZipCode);
  const isNYNoQuotes =
    clientState.stateAbbreviation === 'NY' && noQuotesReason === 'state_not_supported';
  const [showAddPartnerDialog, setShowAddPartnerDialog] = useState(false);
  const [showLinkExistingDialog, setShowLinkExistingDialog] = useState(false);

  const coverageFilteredQuotes = filterQuotesByCoverageType(quotes);

  const { data: underwritingData } = useUnderwritingResults(clientId);
  const quoteIdFromUrl = searchParams.get('quoteId');
  const { topQuotes, remainingQuotes, hasMore } = useQuotesDisplay({
    quotes: coverageFilteredQuotes,
    quoteIdFromUrl,
  });

  const { addQuote, removeQuote, isQuoteAdded, selectedQuote } = useAddPolicyFromQuote();
  const hasSelectedQuote = selectedQuote !== null;

  const selectedQuoteData = selectedQuote?.quote;
  const selectedQuoteId = selectedQuote?.quoteId;

  // If selected quote is from dialog (not in topQuotes), show it first and limit to 4 cards
  const displayedQuotes = useMemo(() => {
    if (!selectedQuoteData) {
      return topQuotes;
    }

    const isSelectedInTop = topQuotes.some(q => getQuoteId(q) === selectedQuoteId);
    if (isSelectedInTop) {
      // Move selected quote to front
      const withoutSelected = topQuotes.filter(q => getQuoteId(q) !== selectedQuoteId);
      return [selectedQuoteData, ...withoutSelected];
    }

    // Selected is from remainingQuotes - prepend and limit to 4
    return [selectedQuoteData, ...topQuotes].slice(0, 4);
  }, [topQuotes, selectedQuoteData, selectedQuoteId]);

  const [showMoreOpen, setShowMoreOpen] = useState(false);
  const [policyTypeFilter, setPolicyTypeFilter] = useState<string>(ALL_POLICY_TYPES);
  const [paymentOptionFilter, setPaymentOptionFilter] = useState<string>(ALL_PAYMENT_OPTIONS);

  // Underwriting slideout state
  const [showUnderwritingSlideOut, setShowUnderwritingSlideOut] = useState(false);
  const [selectedUnderwritingData, setSelectedUnderwritingData] = useState<{
    resultsText: string | null;
    productName: string;
  } | null>(null);

  const handleViewUnderwritingDetails = useCallback(
    (productName: string, resultsText: string | null) => {
      setSelectedUnderwritingData({ resultsText, productName });
      setShowUnderwritingSlideOut(true);
    },
    [],
  );

  const handleSelectQuote = (quote: GuidedPlanningQuote) => {
    setShowMoreOpen(false);
    addQuote(quote);
    setSearchParams(
      prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('quoteId', getQuoteId(quote));
        return newParams;
      },
      { replace: true },
    );
    nextStep();
  };

  const handleRemoveQuote = (quote: GuidedPlanningQuote) => {
    removeQuote(quote);
    setSearchParams(
      prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('quoteId');
        return newParams;
      },
      { replace: true },
    );
  };

  // Base quotes for filtering (excluding selected quote)
  const quotesForFiltering = useMemo(() => {
    return selectedQuoteId
      ? remainingQuotes.filter(quote => getQuoteId(quote) !== selectedQuoteId)
      : remainingQuotes;
  }, [remainingQuotes, selectedQuoteId]);

  // Apply both filters to get final filtered quotes
  const filteredRemainingQuotes = useMemo(() => {
    return quotesForFiltering.filter(quote => {
      if (policyTypeFilter !== ALL_POLICY_TYPES) {
        const selectedPolicyType = Number(policyTypeFilter) as FundingPolicyType;
        if ((quote.fundingPolicyType as FundingPolicyType) !== selectedPolicyType) {
          return false;
        }
      }
      if (paymentOptionFilter !== ALL_PAYMENT_OPTIONS) {
        if (getPaymentOption(quote) !== paymentOptionFilter) {
          return false;
        }
      }
      return true;
    });
  }, [quotesForFiltering, policyTypeFilter, paymentOptionFilter]);

  // Policy type options: show all available types, but with counts reflecting payment filter
  const policyTypeFilterOptions = useMemo(() => {
    const quotesToCount =
      paymentOptionFilter === ALL_PAYMENT_OPTIONS
        ? quotesForFiltering
        : quotesForFiltering.filter(q => getPaymentOption(q) === paymentOptionFilter);
    return getPolicyTypeFilterOptions(quotesToCount);
  }, [quotesForFiltering, paymentOptionFilter]);

  // Payment option options: show all available options, but with counts reflecting policy type filter
  const paymentOptionFilterOptions = useMemo(() => {
    const quotesToCount =
      policyTypeFilter === ALL_POLICY_TYPES
        ? quotesForFiltering
        : quotesForFiltering.filter(
            q =>
              (q.fundingPolicyType as FundingPolicyType) ===
              (Number(policyTypeFilter) as FundingPolicyType),
          );
    return getPaymentOptionFilterOptions(quotesToCount);
  }, [quotesForFiltering, policyTypeFilter]);

  const handleDialogOpenChange = (open: boolean) => {
    setShowMoreOpen(open);
    if (!open) {
      setPolicyTypeFilter(ALL_POLICY_TYPES);
      setPaymentOptionFilter(ALL_PAYMENT_OPTIONS);
    }
  };

  const handleBack = useCallback(() => {
    goToStep(getStepIndex('options'));
  }, [goToStep]);

  const hasQuotes = quotes && quotes.length > 0;
  const hasActiveFilters =
    policyTypeFilter !== ALL_POLICY_TYPES || paymentOptionFilter !== ALL_PAYMENT_OPTIONS;

  const customFooter = (
    <div>
      <GuidedPlanningFooter
        isNextDisabled={!hasSelectedQuote}
        backButtonLabel="Adjust Selection"
        onBack={handleBack}
      />
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
        <ShopInsuranceNotice />
      </div>
    </div>
  );

  return (
    <StepLayout
      title="Highest Modeled ROI for Your Plan and Preferences"
      subtitle="Here's the highest modeled ROI option for each policy type you selected. Click to model how it would work in your plan."
      footer={customFooter}
    >
      {isLoading && <QuotesLoadingState />}

      {isError && <QuotesErrorState />}

      {!isLoading && !isError && !hasQuotes && isNYNoQuotes && (
        <NoQuotesNYScreen
          clientId={clientId}
          advisorId={advisorId}
          signupSource="guided_planning"
        />
      )}

      {!isLoading && !isError && !hasQuotes && !isNYNoQuotes && (
        <QuotesEmptyState
          reason={noQuotesReason}
          filterPanel={
            <QuoteFiltersPanel
              quoteCount={0}
              isRefetching={isRefetching}
            />
          }
        />
      )}

      {!isLoading && !isError && hasQuotes && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <QuoteFiltersPanel
              quoteCount={quotes?.length ?? 0}
              isRefetching={isRefetching}
            />

            {isMarriedOrPartnered && availableTabs.length > 1 && (
              <Tabs
                value={selectedCoverageType}
                onValueChange={handleCoverageTypeChange}
              >
                <TabsList>
                  {availableTabs.map(tab => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      data-testid={`coverage-type-tab-${tab.value}`}
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}
          </div>

          {isRefetching && displayedQuotes.length === 0 && (
            <QuotesLoadingState title="Loading joint policy quotes..." />
          )}

          {!(isRefetching && displayedQuotes.length === 0) && (
            <div
              className={cn(
                'grid gap-4',
                displayedQuotes.length === 1
                  ? 'mx-auto max-w-md grid-cols-1'
                  : 'grid-cols-1 md:grid-cols-2',
              )}
            >
              <div className="col-span-full space-y-3">
                <AiUnderwritingCtaAlert />
              </div>
              {displayedQuotes.map(quote => {
                const isAdded = isQuoteAdded(quote);
                const isBlockedBySelection = hasSelectedQuote && !isAdded;
                const underwritingResult = getUnderwritingResultForQuote(
                  quote.productName,
                  underwritingData?.results,
                  isJointOrCouplesQuote(quote),
                );
                return (
                  <QuoteCard
                    key={getQuoteId(quote)}
                    quote={quote}
                    onSelect={handleSelectQuote}
                    onRemove={handleRemoveQuote}
                    isAlreadyAdded={isAdded}
                    disabled={isSessionComplete || isBlockedBySelection}
                    showSelectionLimitTooltip={isBlockedBySelection}
                    underwritingStatus={underwritingResult?.status}
                    onViewUnderwritingDetails={
                      underwritingResult
                        ? () =>
                            handleViewUnderwritingDetails(
                              quote.productName,
                              underwritingResult.resultsText,
                            )
                        : undefined
                    }
                  />
                );
              })}
            </div>
          )}

          {hasMore && !(isRefetching && displayedQuotes.length === 0) && (
            <div className="flex justify-center pt-4">
              <Button
                variant="link"
                onClick={() => setShowMoreOpen(true)}
              >
                Show More Policy Options
              </Button>
            </div>
          )}

          <Dialog
            open={showMoreOpen}
            onOpenChange={handleDialogOpenChange}
          >
            <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-3xl">
              <DialogHeader>
                <DialogTitle>All Policy Options</DialogTitle>
                <DialogDescription>
                  Additional options sorted by projected return on investment.
                </DialogDescription>
              </DialogHeader>

              {(policyTypeFilterOptions.length > 0 || paymentOptionFilterOptions.length > 0) && (
                <div className="flex flex-wrap items-center gap-3 border-b pb-4">
                  <span className="text-sm font-medium text-gray-700">Filter by:</span>

                  {policyTypeFilterOptions.length > 0 && (
                    <Select
                      value={policyTypeFilter}
                      onValueChange={setPolicyTypeFilter}
                    >
                      <SelectTrigger className="w-60">
                        <SelectValue placeholder="All Product Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_POLICY_TYPES}>
                          <span className="font-medium">All Product Types</span>
                        </SelectItem>
                        {flags.nycLifeDemo
                          ? null
                          : policyTypeFilterOptions.map(option => (
                              <SelectItem
                                key={option.policyType}
                                value={String(option.policyType)}
                              >
                                <div className="flex items-center gap-2">
                                  <img
                                    src={option.logo}
                                    alt={option.carrierName}
                                    className="size-5 object-contain"
                                  />
                                  <span className="font-medium">{option.label}</span>
                                  <span className="text-gray-500">({option.quoteCount})</span>
                                </div>
                              </SelectItem>
                            ))}
                      </SelectContent>
                    </Select>
                  )}

                  {paymentOptionFilterOptions.length > 0 && (
                    <Select
                      value={paymentOptionFilter}
                      onValueChange={setPaymentOptionFilter}
                    >
                      <SelectTrigger className="w-60">
                        <SelectValue placeholder="All Payment Options" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_PAYMENT_OPTIONS}>
                          <span className="font-medium">All Payment Options</span>
                        </SelectItem>
                        {paymentOptionFilterOptions.map(option => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{option.label}</span>
                              <span className="text-gray-500">({option.quoteCount})</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setPolicyTypeFilter(ALL_POLICY_TYPES);
                        setPaymentOptionFilter(ALL_PAYMENT_OPTIONS);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              )}

              <div className="grid gap-4 overflow-y-auto py-4">
                {filteredRemainingQuotes.length > 0 ? (
                  filteredRemainingQuotes.map(quote => {
                    const isAdded = isQuoteAdded(quote);
                    const isBlockedBySelection = hasSelectedQuote && !isAdded;
                    const underwritingResult = getUnderwritingResultForQuote(
                      quote.productName,
                      underwritingData?.results,
                    );
                    return (
                      <QuoteCard
                        key={getQuoteId(quote)}
                        quote={quote}
                        onSelect={handleSelectQuote}
                        onRemove={handleRemoveQuote}
                        isAlreadyAdded={isAdded}
                        disabled={isSessionComplete || isBlockedBySelection}
                        showSelectionLimitTooltip={isBlockedBySelection}
                        underwritingStatus={underwritingResult?.status}
                        onViewUnderwritingDetails={
                          underwritingResult
                            ? () =>
                                handleViewUnderwritingDetails(
                                  quote.productName,
                                  underwritingResult.resultsText,
                                )
                            : undefined
                        }
                      />
                    );
                  })
                ) : (
                  <p className="py-8 text-center text-gray-500">
                    No quotes match the selected filter.
                  </p>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Underwriting Analysis Slideout */}
      {!flags.nycLifeDemo && (
        <UnderwritingSlideOut
          open={showUnderwritingSlideOut}
          onClose={() => setShowUnderwritingSlideOut(false)}
          underwritingData={
            selectedUnderwritingData?.resultsText
              ? ({ results: selectedUnderwritingData.resultsText } as OpenAiCompletionDbRecord)
              : null
          }
          productName={selectedUnderwritingData?.productName ?? ''}
        />
      )}

      <PartnerRequiredDialog
        open={showPartnerRequiredDialog}
        onOpenChange={setShowPartnerRequiredDialog}
        onTakeMeToIt={() => {
          setShowPartnerRequiredDialog(false);
          setShowAddPartnerDialog(true);
        }}
        onLinkExisting={
          isAdvisor
            ? () => {
                setShowPartnerRequiredDialog(false);
                setShowLinkExistingDialog(true);
              }
            : undefined
        }
      />

      {isAdvisor && (
        <LinkExistingPartnerDialog
          open={showLinkExistingDialog}
          client={client}
          onClose={() => setShowLinkExistingDialog(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: guidedPlanningQuoteKeys.all });
            setSelectedCoverageType('joint');
          }}
        />
      )}

      <AddPartnerDialog
        open={showAddPartnerDialog}
        onOpenChange={setShowAddPartnerDialog}
        clientId={clientId}
        onSubmitSuccess={() => {
          setShowAddPartnerDialog(false);
          queryClient.invalidateQueries({ queryKey: guidedPlanningQuoteKeys.all });
          setSelectedCoverageType('joint');
        }}
      />
    </StepLayout>
  );
}
